/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICreateActionContext, IGeneric, IRequestContext } from '@msdyn365-commerce/core';
import { QueryResultSettings, SortColumn } from '@msdyn365-commerce/retail-proxy';

import { IQueryResultSettingsModuleConfig } from './IQueryResultSettingsModuleConfig';

/**
 * Wrapper for query result settings, which guarantees some properties that are optional in the real subject
 * and includes some common utilities for data actions.
 */
export class QueryResultSettingsProxy {
    // Real subject
    private readonly queryResultSettings: QueryResultSettings;

    /**
     * Factory method for data actions.
     * @param inputData - Input data.
     * @returns - Returns query result settings proxy.
     */
    public static fromInputData(inputData: ICreateActionContext<IGeneric<IAny>>): QueryResultSettingsProxy {
        const { requestContext, config } = inputData;
        return QueryResultSettingsProxy.fromModuleData(requestContext, config as IQueryResultSettingsModuleConfig);
    }

    /**
     * Factory method for modules.
     * @param requestContext - RequestContext.
     * @param config - Module config from props.
     * @returns - Returns query result settings proxy.
     */
    public static fromModuleData(requestContext: IRequestContext, config: IQueryResultSettingsModuleConfig): QueryResultSettingsProxy {
        const sortingCriteria = (config && config.sortingCriteria) || undefined;
        const query = requestContext && requestContext.query;
        const top = (query && query.top) || undefined;
        const skip = (query && query.skip) || undefined;

        const queryResultSettings = {
            Paging: {
                Skip: (skip && Number(skip)) || 0,
                Top: (top && Number(top)) || config?.itemsPerPage || 50
            },
            Sorting: sortingCriteria || {}
        };

        return new QueryResultSettingsProxy(queryResultSettings);
    }

    /**
     * Return the query result settings with default paging or with the passed value.
     * @param inputData - Input Data.
     * @returns - Query Result settings.
     */
    public static getPagingFromInputDataOrDefaultValue(inputData: ICreateActionContext<IGeneric<IAny>>): QueryResultSettings {
        const config: IQueryResultSettingsModuleConfig | undefined = inputData.config;
        const defaultPageSize: number = 100;
        const queryResultSettings: QueryResultSettings = {
            Paging: {
                // eslint-disable-next-line max-len -- Disable max length for next line.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unnecessary-condition -- Reading from config.
                Top:
                    config?.itemsPerPage ??
                    Number.parseInt(inputData.requestContext?.app?.platform?.defaultPageSizeForAPI ?? defaultPageSize, 10)
            }
        };
        return queryResultSettings;
    }

    /**
     * Factory method.
     * @returns - Returns query result settings proxy.
     */
    public static getDefault(): QueryResultSettingsProxy {
        return new QueryResultSettingsProxy({ Paging: {}, Sorting: {} });
    }

    /**
     * Private constructor, expect instances to be created with the factory method.
     * @param queryResultSettings - Query result setting.
     */
    private constructor(queryResultSettings: QueryResultSettings) {
        this.queryResultSettings = queryResultSettings;
    }

    public get QueryResultSettings(): QueryResultSettings {
        return this.queryResultSettings;
    }

    /**
     * Gets the hints for the cache key based on query result settings.
     * @returns - Returns cache key hint.
     */
    public get cacheKeyHint(): string {
        // The factory method guarantees values are defined
        const topHint = `top-${this.queryResultSettings.Paging!.Top!}`;
        const skipHint = `skip-${this.queryResultSettings.Paging!.Skip!}`;
        const sortingHintData =
            (this.queryResultSettings.Sorting!.Columns || [])
                .map((column: SortColumn) => {
                    return `${column.ColumnName}--${(column.IsDescending && 'desc') || 'asc'}`;
                })
                .join(',') || undefined;
        const sortingHint = `sortBy-${sortingHintData}`;
        return `${topHint}|${skipHint}|${sortingHint}`;
    }
}
